import Fuse from 'fuse.js';

const VENUE_SEARCH_TEXT_FIELDS = [
  'stage', 'power', 'lightingControl', 'lightingFixtures', 'video', 'foh',
  'pa', 'monitors', 'backstage', 'backline', 'merch', 'parking', 'shorePower',
  'notes', 'description', 'venueType', 'capacity',
];

const VENUE_SEARCH_FIELD_LABELS = {
  notes: 'Notes',
  description: 'Description',
  capacity: 'Capacity',
  venueType: 'Venue type',
  stage: 'Stage',
  power: 'Power',
  lightingControl: 'Lighting',
  lightingFixtures: 'Lighting',
  video: 'Video',
  foh: 'FOH',
  pa: 'PA',
  monitors: 'Monitors',
  backstage: 'Backstage',
  backline: 'Backline',
  merch: 'Merch',
  parking: 'Parking',
  shorePower: 'Shore power',
};

// Configure Fuse.js options for better fuzzy searching
const fuseOptions = {
  keys: [
    { name: 'name', weight: 0.35 },
    { name: 'city', weight: 0.2 },
    { name: 'state', weight: 0.1 },
    { name: 'affiliate', weight: 0.15 },
    { name: 'fullAddress', weight: 0.08 },
    { name: 'address', weight: 0.08 },
    { name: 'notes', weight: 0.02 },
    { name: 'description', weight: 0.02 },
  ],
  threshold: 0.3,
  distance: 100,
  minMatchCharLength: 2,
};

// US state / DC abbreviations ↔ full names (lowercase). Used so queries like
// "Austin TX" and "Austin, Texas" resolve to the same venue set.
export const US_STATE_ABBR_TO_NAME = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
  co: 'colorado', ct: 'connecticut', de: 'delaware', dc: 'district of columbia',
  fl: 'florida', ga: 'georgia', hi: 'hawaii', id: 'idaho', il: 'illinois',
  in: 'indiana', ia: 'iowa', ks: 'kansas', ky: 'kentucky', la: 'louisiana',
  me: 'maine', md: 'maryland', ma: 'massachusetts', mi: 'michigan', mn: 'minnesota',
  ms: 'mississippi', mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada',
  nh: 'new hampshire', nj: 'new jersey', nm: 'new mexico', ny: 'new york',
  nc: 'north carolina', nd: 'north dakota', oh: 'ohio', ok: 'oklahoma', or: 'oregon',
  pa: 'pennsylvania', ri: 'rhode island', sc: 'south carolina', sd: 'south dakota',
  tn: 'tennessee', tx: 'texas', ut: 'utah', vt: 'vermont', va: 'virginia',
  wa: 'washington', wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
};

export const US_STATE_NAME_TO_ABBR = Object.fromEntries(
  Object.entries(US_STATE_ABBR_TO_NAME).map(([abbr, name]) => [name, abbr]),
);

/** Collapse punctuation/spacing so "Austin, Texas" ≡ "austin texas". */
export function normalizeLocationText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Canonical full state name for a token (abbr or name), or null if unknown. */
export function canonicalizeStateToken(token) {
  const key = normalizeLocationText(token);
  if (!key) return null;
  if (US_STATE_ABBR_TO_NAME[key]) return US_STATE_ABBR_TO_NAME[key];
  if (US_STATE_NAME_TO_ABBR[key]) return key;
  return null;
}

/** All searchable forms for a state token (abbr + full name). */
export function stateSynonymForms(token) {
  const canonical = canonicalizeStateToken(token);
  if (!canonical) return token ? [normalizeLocationText(token)] : [];
  const abbr = US_STATE_NAME_TO_ABBR[canonical];
  return [...new Set([canonical, abbr].filter(Boolean))];
}

/** True when two state strings refer to the same place (TX ↔ Texas). */
export function statesMatch(a, b) {
  if (!a || !b) return false;
  const aCanon = canonicalizeStateToken(a) || normalizeLocationText(a);
  const bCanon = canonicalizeStateToken(b) || normalizeLocationText(b);
  if (aCanon === bCanon) return true;
  const aForms = stateSynonymForms(a);
  const bNorm = normalizeLocationText(b);
  // Whole words only, so a short code inside a word ("medows" has "me" = Maine) is not a match.
  return aForms.some((form) => form === bNorm || ` ${bNorm} `.includes(` ${form} `) || ` ${form} `.includes(` ${bNorm} `));
}

/**
 * Expand a single query term with state synonyms so "tx" also matches "texas".
 */
export function expandSearchTerm(term) {
  const normalized = normalizeLocationText(term);
  if (!normalized) return [];
  const synonyms = stateSynonymForms(normalized);
  return [...new Set([normalized, ...synonyms])];
}

/**
 * Detect "City, ST", "City ST", "City, State", "City State" (no near/in required).
 * Tries trailing US state abbr (1 token) or full name (1–3 tokens).
 */
export function parseCityStateQuery(query) {
  const normalized = normalizeLocationText(query);
  if (!normalized) return null;
  const tokens = normalized.split(' ').filter(Boolean);
  if (tokens.length < 2) return null;

  // Prefer 2-letter trailing abbreviation (Austin TX, Austin, TX).
  const last = tokens[tokens.length - 1];
  if (last.length === 2 && US_STATE_ABBR_TO_NAME[last]) {
    const city = tokens.slice(0, -1).join(' ');
    if (!city) return null;
    return {
      type: 'location',
      city,
      state: US_STATE_ABBR_TO_NAME[last],
      stateRaw: last,
      original: query,
    };
  }

  // Then trailing full state name (Austin Texas / … New York).
  for (let len = Math.min(3, tokens.length - 1); len >= 1; len -= 1) {
    const candidate = tokens.slice(-len).join(' ');
    const canonical = canonicalizeStateToken(candidate);
    if (!canonical) continue;
    const city = tokens.slice(0, -len).join(' ');
    if (!city) continue;
    return {
      type: 'location',
      city,
      state: canonical,
      stateRaw: candidate,
      original: query,
    };
  }

  return null;
}

function venueMatchesParsedLocation(venue, location) {
  if (!location) return true;
  const hasCity = Boolean(location.city);
  const hasState = Boolean(location.state || location.stateRaw);
  if (!hasCity && !hasState) return true;

  const venueCity = normalizeLocationText(venue.city);
  const venueState = venue.state || '';

  if (hasCity) {
    const cityOk = venueCity.includes(location.city) || location.city.includes(venueCity);
    if (!cityOk) return false;
  }
  if (hasState) {
    const stateOk = statesMatch(venueState, location.state)
      || statesMatch(venueState, location.stateRaw);
    if (!stateOk) return false;
  }
  return true;
}

/** Location string variants for a venue (city + state name/abbr, with/without comma). */
function venueLocationVariants(city, state) {
  if (!city) return [];
  const forms = new Set([normalizeLocationText(`${city} ${state || ''}`)]);
  if (state) {
    stateSynonymForms(state).forEach((s) => {
      forms.add(normalizeLocationText(`${city} ${s}`));
      forms.add(normalizeLocationText(`${city}, ${s}`));
    });
  }
  return [...forms].filter(Boolean);
}

export function buildVenueSearchHaystack(venue) {
  const name = (venue?.name || '').toLowerCase();
  const city = (venue?.city || '').toLowerCase();
  const state = (venue?.state || '').toLowerCase();
  const affiliate = (venue?.affiliate || '').toLowerCase();
  const address = (venue?.fullAddress || venue?.address || '').toLowerCase();
  const stateForms = stateSynonymForms(state).join(' ');
  const extra = VENUE_SEARCH_TEXT_FIELDS
    .map((field) => venue?.[field])
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');
  const searchable = [name, city, state, stateForms, affiliate, address, extra]
    .filter(Boolean)
    .join(' ');
  return { name, city, state, affiliate, address, searchable, stateForms };
}

function textMatchesQuery(text, queryTerms, fullQuery) {
  if (!text) return false;
  const normalizedText = normalizeLocationText(text);
  if (fullQuery && (text.includes(fullQuery) || normalizedText.includes(normalizeLocationText(fullQuery)))) {
    return true;
  }
  return queryTerms.some((term) => {
    const forms = expandSearchTerm(term);
    return forms.some((form) => text.includes(form) || normalizedText.includes(form));
  });
}

function termMatchesField(fieldText, term) {
  if (!fieldText || !term) return false;
  return expandSearchTerm(term).some((form) => fieldText.includes(form));
}

function scoreVenueForSearch(venue, queryTerms, fullQuery) {
  const { name, city, state, affiliate, address, searchable } = buildVenueSearchHaystack(venue);
  let score = 0;
  const matchReasons = [];
  const addReason = (reason) => {
    if (reason && !matchReasons.includes(reason)) matchReasons.push(reason);
  };

  const normalizedQuery = normalizeLocationText(fullQuery);

  if (name === fullQuery || name === normalizedQuery) {
    score += 10000;
    addReason('Name');
  } else if (name.startsWith(fullQuery) || (normalizedQuery && name.startsWith(normalizedQuery))) {
    score += 5000;
    addReason('Name');
  } else if (name.includes(fullQuery) || (normalizedQuery && name.includes(normalizedQuery))) {
    score += 800;
    addReason('Name');
  }

  if (address && (address.includes(fullQuery) || (normalizedQuery && normalizeLocationText(address).includes(normalizedQuery)))) {
    score += 800;
    addReason('Address');
  }

  const locationVariants = venueLocationVariants(city, state);
  if (normalizedQuery && locationVariants.some((variant) => variant === normalizedQuery || variant.includes(normalizedQuery))) {
    score += 600;
    addReason(venue.city ? `City · ${venue.city}` : 'City');
    if (venue.state) addReason(`State · ${venue.state}`);
  }

  queryTerms.forEach((term) => {
    if (termMatchesField(name, term)) {
      score += 400;
      addReason('Name');
    }
    if (termMatchesField(city, term)) {
      score += 300;
      addReason(venue.city ? `City · ${venue.city}` : 'City');
    }
    if (termMatchesField(state, term) || stateSynonymForms(state).some((form) => expandSearchTerm(term).includes(form))) {
      score += 100;
      addReason(venue.state ? `State · ${venue.state}` : 'State');
    }
    if (termMatchesField(affiliate, term)) {
      score += 200;
      addReason(venue.affiliate ? `Affiliate · ${venue.affiliate}` : 'Affiliate');
    }
    if (termMatchesField(address, term)) {
      score += 300;
      addReason('Address');
    }
  });

  VENUE_SEARCH_TEXT_FIELDS.forEach((field) => {
    const raw = venue?.[field];
    if (!raw) return;
    const value = String(raw).toLowerCase();
    if (!textMatchesQuery(value, queryTerms, fullQuery)) return;
    const label = VENUE_SEARCH_FIELD_LABELS[field] || field;
    if (field === 'capacity' && venue.capacity) {
      addReason(`Capacity · ${venue.capacity}`);
      return;
    }
    if (field === 'venueType' && venue.venueType) {
      addReason(`Type · ${venue.venueType}`);
      return;
    }
    addReason(label);
  });

  const allTermsMatch = queryTerms.length > 0 && queryTerms.every((term) => (
    expandSearchTerm(term).some((form) => searchable.includes(form))
  ));
  if (allTermsMatch && score === 0) {
    score += 50;
    addReason('Multiple fields');
  }

  return { score, allTermsMatch, matchReasons };
}

export function describeVenueSearchMatches(venue, query) {
  if (!query || !String(query).trim()) {
    return { score: 0, allTermsMatch: false, matchReasons: [] };
  }
  const parsed = parseNaturalLanguageQuery(String(query).trim());
  const searchText = (parsed.remaining || String(query).trim()).toLowerCase();
  const queryTerms = normalizeLocationText(searchText).split(/\s+/).filter(Boolean);
  return scoreVenueForSearch(venue, queryTerms, searchText);
}

export function getSearchHighlightTerms(query) {
  if (!query || !String(query).trim()) return [];
  const parsed = parseNaturalLanguageQuery(String(query).trim());
  const searchText = (parsed.remaining || String(query).trim()).toLowerCase();
  const terms = searchText.split(/\s+/).filter((term) => term.length > 0);
  const unique = new Set([searchText, normalizeLocationText(searchText), ...terms].filter(Boolean));
  // Include state synonyms so searching "TX" still highlights "Texas" in results.
  [...terms].forEach((term) => {
    expandSearchTerm(term).forEach((form) => unique.add(form));
  });
  if (parsed.location?.state) {
    stateSynonymForms(parsed.location.state).forEach((form) => unique.add(form));
  }
  if (parsed.location?.city) unique.add(parsed.location.city);
  return [...unique].sort((a, b) => b.length - a.length);
}

function mergeHighlightRanges(ranges) {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push(current);
    }
  }
  return merged;
}

export function buildHighlightedTextSegments(text, query) {
  const source = text == null ? '' : String(text);
  if (!source) return [{ text: '', highlighted: false }];
  if (!query || !String(query).trim()) {
    return [{ text: source, highlighted: false }];
  }

  const terms = getSearchHighlightTerms(query);
  if (!terms.length) return [{ text: source, highlighted: false }];

  const lower = source.toLowerCase();
  let ranges = [];
  terms.forEach((term) => {
    let pos = 0;
    while (pos < lower.length) {
      const index = lower.indexOf(term, pos);
      if (index === -1) break;
      ranges.push({ start: index, end: index + term.length });
      pos = index + term.length;
    }
  });

  ranges = mergeHighlightRanges(ranges);
  if (!ranges.length) return [{ text: source, highlighted: false }];

  const segments = [];
  let cursor = 0;
  ranges.forEach(({ start, end }) => {
    if (start > cursor) {
      segments.push({ text: source.slice(cursor, start), highlighted: false });
    }
    segments.push({ text: source.slice(start, end), highlighted: true });
    cursor = end;
  });
  if (cursor < source.length) {
    segments.push({ text: source.slice(cursor), highlighted: false });
  }
  return segments;
}

export function getVenueSearchDetailParts(venue) {
  const location = [venue?.city, venue?.state].filter(Boolean).join(', ');
  const address = (venue?.fullAddress || venue?.address || '').trim();
  const affiliate = venue?.affiliate?.trim() || '';
  const capacity = venue?.capacity ? String(venue.capacity).trim() : '';
  const parts = [];
  if (location) parts.push(location);
  if (address) parts.push(address);
  if (affiliate) parts.push(affiliate);
  if (capacity) parts.push(`Cap ${capacity}`);
  return parts;
}

export function formatVenueSearchResultMeta(venue) {
  const detailParts = getVenueSearchDetailParts(venue);
  return {
    detailLine: detailParts.join(' · '),
    detailParts,
    affiliate: venue?.affiliate?.trim() || '',
  };
}

export function isVenueWithinLngLatBounds(venue, bounds) {
  if (!bounds || !venue) return false;
  const lat = parseFloat(venue.latitude);
  const lng = parseFloat(venue.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  return (
    lat >= bounds.getSouth() &&
    lat <= bounds.getNorth() &&
    lng >= bounds.getWest() &&
    lng <= bounds.getEast()
  );
}

/** In-map-view matches first; out-of-view kept for context with lower UI opacity. */
export function sortSearchResultsByMapVicinity(venues, bounds) {
  if (!Array.isArray(venues) || venues.length === 0) return [];
  if (!bounds) {
    return venues.map((venue) => ({ venue, inMapView: true }));
  }
  const inView = [];
  const outOfView = [];
  venues.forEach((venue) => {
    if (isVenueWithinLngLatBounds(venue, bounds)) {
      inView.push(venue);
    } else {
      outOfView.push(venue);
    }
  });
  return [
    ...inView.map((venue) => ({ venue, inMapView: true })),
    ...outOfView.map((venue) => ({ venue, inMapView: false })),
  ];
}

function attachSearchMatchMeta(venue, query, score, matchReasons) {
  const reasons = matchReasons?.length
    ? matchReasons
    : describeVenueSearchMatches(venue, query).matchReasons;
  return {
    ...venue,
    _searchScore: score,
    _searchMatchReasons: reasons,
  };
}

export function searchVenuesByQuery(venues, query, { limit = 500 } = {}) {
  if (!Array.isArray(venues) || venues.length === 0) return [];
  if (!query || !String(query).trim()) return venues;

  const trimmed = String(query).trim();
  const parsed = parseNaturalLanguageQuery(trimmed);
  let pool = [...venues];

  if (parsed.location) {
    pool = pool.filter((venue) => venueMatchesParsedLocation(venue, parsed.location));
  }

  if (parsed.capacity) {
    pool = pool.filter((venue) => {
      const capacity = parseInt(String(venue.capacity || '').replace(/[^0-9]/g, ''), 10) || 0;
      return parsed.capacity.operator === '>'
        ? capacity > parsed.capacity.value
        : capacity < parsed.capacity.value;
    });
  }

  // City/state-only queries (e.g. "Austin TX") already filtered the pool —
  // return those ranked by score when there's no leftover name text.
  const searchText = parsed.remaining || (parsed.location ? '' : trimmed);
  if (!searchText) {
    if (parsed.location) {
      return pool
        .map((venue) => {
          const { score, matchReasons } = scoreVenueForSearch(
            venue,
            [parsed.location.city, parsed.location.state].filter(Boolean),
            normalizeLocationText(`${parsed.location.city} ${parsed.location.state || ''}`),
          );
          return { venue, score: Math.max(score, 1), matchReasons };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(({ venue, score, matchReasons }) => attachSearchMatchMeta(venue, trimmed, score, matchReasons));
    }
    return pool.slice(0, limit);
  }

  const fullQuery = searchText.toLowerCase();
  const queryTerms = normalizeLocationText(fullQuery).split(/\s+/).filter(Boolean);

  const scored = pool
    .map((venue) => {
      const { score, allTermsMatch, matchReasons } = scoreVenueForSearch(venue, queryTerms, fullQuery);
      const finalScore = score + (allTermsMatch ? 25 : 0);
      return { venue, score: finalScore, matchReasons };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ venue, score, matchReasons }) => attachSearchMatchMeta(venue, trimmed, score, matchReasons));

  if (scored.length > 0) {
    return scored.slice(0, limit);
  }

  return performFuzzySearch(venues, trimmed)
    .slice(0, limit)
    .map((venue) => attachSearchMatchMeta(venue, trimmed, 0, []));
}

// Parse location queries like "near city, state", "in state", or bare "City ST" / "City, State"
export function parseLocationQuery(query) {
  const trimmed = String(query || '').trim();
  const startsWithNearIn = /^(near|in)\b/i.test(trimmed);

  // Prefer bare "City ST" / "City, State" when the query is not a near/in phrase,
  // so city names ending in "in" (Austin) are not eaten by that pattern.
  if (!startsWithNearIn) {
    const bare = parseCityStateQuery(trimmed);
    if (bare?.city && !/^(near|in)$/i.test(bare.city)) return bare;
  }

  // Word-boundary required: otherwise "Austin TX" matches as "in TX".
  const nearInRegex = /\b(?:near|in)\s+(.+)$/i;
  const nearInMatch = trimmed.match(nearInRegex);
  if (nearInMatch) {
    const locationPart = nearInMatch[1].trim();
    const parsed = parseCityStateQuery(locationPart);
    if (parsed) {
      return { ...parsed, original: nearInMatch[0].trim() };
    }
    // "in Texas" / "near Austin" without a trailing state token
    const onlyState = canonicalizeStateToken(locationPart);
    if (onlyState) {
      return {
        type: 'location',
        city: '',
        state: onlyState,
        stateRaw: normalizeLocationText(locationPart),
        original: nearInMatch[0].trim(),
      };
    }
    return {
      type: 'location',
      city: normalizeLocationText(locationPart),
      state: null,
      original: nearInMatch[0].trim(),
    };
  }

  return null;
}

// Parse capacity queries like "capacity > 1000" or "less than 500 capacity"
export function parseCapacityQuery(query) {
  const capacityRegex = /(?:capacity\s*([<>])\s*(\d+))|(?:(less|more)\s+than\s+(\d+)\s+capacity)/i;
  const match = query.match(capacityRegex);

  if (match) {
    const operator = match[1] || (match[3] === 'less' ? '<' : '>');
    const value = parseInt(match[2] || match[4], 10);
    return {
      type: 'capacity',
      operator,
      value,
      original: match[0],
    };
  }
  return null;
}

export function parseNaturalLanguageQuery(query) {
  const capacityMatch = parseCapacityQuery(query);
  let working = query;
  if (capacityMatch?.original) {
    working = working.replace(capacityMatch.original, ' ').trim();
  }

  const locationMatch = parseLocationQuery(working);

  // When the whole query is a city/state location, consume it so remaining is empty
  // (avoids double-filtering as both location and free-text terms).
  let remaining = working;
  if (locationMatch?.original) {
    if (
      locationMatch.original === working
      || normalizeLocationText(locationMatch.original) === normalizeLocationText(working)
    ) {
      remaining = '';
    } else {
      remaining = working.replace(locationMatch.original, ' ').trim();
    }
  }

  return {
    location: locationMatch,
    capacity: capacityMatch,
    remaining: remaining.replace(/\s+/g, ' ').trim(),
  };
}

export function performFuzzySearch(venues, query) {
  try {
    const parsedQuery = parseNaturalLanguageQuery(query);
    let filtered = [...venues];

    // Handle location filtering (abbr ↔ full name aware)
    if (parsedQuery.location) {
      filtered = filtered.filter((venue) => venueMatchesParsedLocation(venue, parsedQuery.location));
    }

    // Handle capacity filtering
    if (parsedQuery.capacity) {
      filtered = filtered.filter((venue) => {
        const capacity = parseInt(venue.capacity?.toString().replace(/[^0-9]/g, '') || '0', 10);
        return parsedQuery.capacity.operator === '>'
          ? capacity > parsedQuery.capacity.value
          : capacity < parsedQuery.capacity.value;
      });
    }

    // If there's remaining text, use fuzzy search
    if (parsedQuery.remaining) {
      const searchLower = parsedQuery.remaining.toLowerCase();
      const searchNormalized = normalizeLocationText(parsedQuery.remaining);

      // Check for exact matches first (prioritize these at the top)
      const exactMatches = filtered.filter((venue) =>
        (venue.name || '').toLowerCase() === searchLower
        || (venue.city || '').toLowerCase() === searchLower
        || (venue.state || '').toLowerCase() === searchLower
        || (canonicalizeStateToken(searchNormalized) && statesMatch(venue.state, searchNormalized))
        || (venue.affiliate || '').toLowerCase() === searchLower
      );

      // Then check for venues whose names start with the search term
      const startsWithMatches = filtered.filter((venue) => {
        const venueName = (venue.name || '').toLowerCase();
        return venueName.startsWith(searchLower) && venueName !== searchLower;
      });

      // Use Fuse.js for the rest (fuzzy matching)
      const fuse = new Fuse(filtered.filter((venue) =>
        !exactMatches.includes(venue)
        && !startsWithMatches.includes(venue)
      ), fuseOptions);

      const fuzzyMatches = fuse.search(parsedQuery.remaining).map((result) => result.item);

      // Combine all results, prioritizing exact matches, then startsWith matches, then fuzzy matches
      return [...exactMatches, ...startsWithMatches, ...fuzzyMatches];
    }

    return filtered;
  } catch (error) {
    console.error('Search error:', error);
    return venues; // Return all venues if search fails
  }
}
